import { accessTokenMemory } from "@/features/auth/services/accessTokenMemory";
import { authSessionBus } from "@/shared/lib/authSessionBus";

// The API rotates the refresh cookie on every refresh and revokes the access
// token it replaces. Every tab shares the cookie but keeps its own token in
// memory: refreshes are serialized across tabs (Web Locks), and the token a
// refresh produces is published to the other tabs (BroadcastChannel), so that
// a waiting tab reuses it instead of rotating the cookie again — a second
// rotation with the same cookie loses the race (401 session_expired).
export const AUTH_CHANNEL_NAME = "yeati-benef-auth";
// The name the previous version (token in the localStorage) already used: during a
// deployment, a tab still on that version and an updated one serialize together.
export const REFRESH_LOCK_NAME = "yeati_benef_token_refresh";

// A token published by another tab is reused only while it has this much life left.
export const SHARED_TOKEN_MIN_REMAINING_MS = 60_000;

// How long a tab that waited for the lock waits for the token of the tab that held it.
export const SHARED_TOKEN_WAIT_MS = 1_000;

type AuthMessage =
  | { type: "token", accessToken: string, expiresAt: number | null }
  | { type: "logout" };

export type FreshAccessToken = { accessToken: string, expiresAt: number | null };

let channel: BroadcastChannel | null | undefined;
let lastShared: FreshAccessToken | null = null;
let localRefreshQueue: Promise<unknown> = Promise.resolve();
let localRefreshesPending = 0;
const messageWaiters = new Set<() => void>();

const isAuthMessage = (data: unknown): data is AuthMessage => {
  if (!data || typeof data !== "object" || !("type" in data)) return false;
  if (data.type === "logout") return true;
  return data.type === "token"
    && "accessToken" in data && typeof data.accessToken === "string" && !!data.accessToken
    && "expiresAt" in data && (data.expiresAt === null || typeof data.expiresAt === "number");
};

const onMessage = ({ data }: MessageEvent<unknown>): void => {
  if (!isAuthMessage(data)) return;

  if (data.type === "token") {
    lastShared = { accessToken: data.accessToken, expiresAt: data.expiresAt };
    // The refresh of the other tab revoked the token this tab holds: adopt the new one.
    if (accessTokenMemory.getAccessToken()) accessTokenMemory.setAccessToken(data.accessToken, data.expiresAt);
    messageWaiters.forEach((wake) => wake());
    return;
  }

  lastShared = null;
  messageWaiters.forEach((wake) => wake());
  // Nothing to end in a tab that holds no session (login page, refresh pending).
  if (!accessTokenMemory.getAccessToken()) return;
  // Cleared first: the logout that follows then neither calls the API nor broadcasts again.
  accessTokenMemory.clear();
  authSessionBus.triggerUnauthorized();
};

const getChannel = (): BroadcastChannel | null => {
  if (channel !== undefined) return channel;
  channel = null;
  if (typeof BroadcastChannel !== "function") return channel;
  try {
    channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    channel.addEventListener("message", onMessage);
    // Node (tests) keeps the process alive while a channel is open; browsers have no unref.
    (channel as BroadcastChannel & { unref?: () => void }).unref?.();
  }
  catch {
    channel = null;
  }
  return channel;
};

const post = (message: AuthMessage): void => {
  try {
    getChannel()?.postMessage(message);
  }
  catch {
    // A tab that misses the message falls back on its own refresh.
  }
};

// Listens to the other tabs from the start, so that a token they publish is known before this tab needs one.
export const connectAuthTokenSync = (): void => {
  getChannel();
};

export const publishAccessToken = (accessToken: string, expiresAt: number | null): void => {
  post({ type: "token", accessToken, expiresAt });
};

export const publishLogout = (): void => {
  lastShared = null;
  post({ type: "logout" });
};

const hasMinimumLife = (expiresAt: number | null): boolean =>
  expiresAt !== null && expiresAt - Date.now() > SHARED_TOKEN_MIN_REMAINING_MS;

// A token obtained since `staleToken` was read — by another refresh of this tab,
// or published by another tab — that can be used instead of refreshing again.
export const findFreshAccessToken = (staleToken: string | null): FreshAccessToken | null => {
  const held = accessTokenMemory.getAccessToken();
  if (held && held !== staleToken) {
    const expiresAt = accessTokenMemory.getExpiresAt();
    if (expiresAt === null || hasMinimumLife(expiresAt)) return { accessToken: held, expiresAt };
  }

  if (lastShared && lastShared.accessToken !== staleToken && hasMinimumLife(lastShared.expiresAt)) {
    return { ...lastShared };
  }

  return null;
};

// Waits, within a bound, for the token of the tab that held the lock before
// this one: the lock and the channel are independent, and the lock can be
// granted before the message published under it is delivered.
export const waitForFreshAccessToken = (staleToken: string | null, timeoutMs = SHARED_TOKEN_WAIT_MS): Promise<FreshAccessToken | null> => {
  const found = findFreshAccessToken(staleToken);
  if (found) return Promise.resolve(found);

  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      messageWaiters.delete(wake);
      resolve(findFreshAccessToken(staleToken));
    };
    // Any message ends the wait: a token, or a logout after which there is nothing to wait for.
    const wake = () => finish();
    const timer = setTimeout(finish, timeoutMs);
    messageWaiters.add(wake);
  });
};

const NOT_ACQUIRED = Symbol("refresh lock not acquired");

// Runs `task` while no other tab refreshes; `contended` tells whether another
// tab (or another refresh of this tab) held the lock first. Without Web Locks
// (older browsers), only the refreshes of this tab are serialized; a token
// another tab publishes is still picked up by findFreshAccessToken.
export const withRefreshLock = async <T>(task: (contended: boolean) => Promise<T>): Promise<T> => {
  getChannel();

  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request) {
    const immediate = await locks.request(REFRESH_LOCK_NAME, { ifAvailable: true }, (lock) => (lock ? task(false) : NOT_ACQUIRED));
    if (immediate !== NOT_ACQUIRED) return immediate;
    return locks.request(REFRESH_LOCK_NAME, () => task(true));
  }

  const contended = localRefreshesPending > 0;
  localRefreshesPending += 1;
  const run = localRefreshQueue.then(() => task(contended), () => task(contended));
  localRefreshQueue = run.catch(() => undefined).finally(() => {
    localRefreshesPending -= 1;
  });
  return run;
};
